import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { AuthorizationService } from './authorization.service';
import { UserNotificationListByUserIdItemPagedResponse, UserNotificationListItemPagedResponse } from 'types/user.interface';
export const MINUTE = 60 * 1000; // 1 minute in ms
export const NOTIFICATION_POLL_INTERVAL = 5 * MINUTE;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<UserNotificationListByUserIdItemPagedResponse | null>(null);
  notifications$ = this.notificationsSubject.asObservable();

  private pollingSubscription?: Subscription;
  private readonly POLLING_INTERVAL = NOTIFICATION_POLL_INTERVAL;
  private activeSubscribers: number = 0;

  constructor(
    private apiService: ApiService,
    private authService: AuthorizationService
  ) {}

  startPolling(): void {
    this.activeSubscribers++;

    // Only start polling if this is the first subscriber
    if (this.activeSubscribers === 1) {
      this.fetchNotifications();

      // Set up polling
      this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
        this.fetchNotifications();
      });
    }
  }

  stopPolling(): void {
    this.activeSubscribers--;

    // Only stop polling if there are no more subscribers
    if (this.activeSubscribers === 0) {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
        this.pollingSubscription = undefined;
      }
      // Clear the notifications when no one is listening
      this.notificationsSubject.next(null);
    }
  }

  private fetchNotifications(): void {
    if (this.authService.isAuthenticated()) {
      const userId = this.authService.getUserId();
      if (userId) {
        this.apiService.getAllUserNotifications(userId).subscribe(
          (data: UserNotificationListByUserIdItemPagedResponse) => {
            this.notificationsSubject.next(data);
          },
          (error: any) => {
            console.error(error);
          }
        );
      }
    }
  }

  getNotifications(): Observable<UserNotificationListItemPagedResponse | null> {
    return this.notifications$;
  }

  markAsRead(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.getValue();

    if (currentNotifications) {
      const updatedNotifications = {
        ...currentNotifications,
        list: currentNotifications.list?.map((notification) =>
          notification.id === notificationId ? { ...notification, isRead: true } : notification
        )
      };

      this.notificationsSubject.next(updatedNotifications);
    }
  }

  markAsUnread(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.getValue();

    if (currentNotifications) {
      const updatedNotifications = {
        ...currentNotifications,
        list: currentNotifications.list?.map((notification) =>
          notification.id === notificationId ? { ...notification, isRead: false } : notification
        )
      };

      this.notificationsSubject.next(updatedNotifications);
    }
  }
}
